// composables/usePhoneUtils.ts
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  AsYouType,
} from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";

export function usePhoneUtils() {
  /**
   * Removes leading zeros from a phone number string
   */
  const removeLeadingZeros = (phoneNumber: string): string => {
    return phoneNumber.replace(/^0+/, "");
  };

  /**
   * Checks if a phone number has leading zeros
   */
  const hasLeadingZeros = (phoneNumber: string): boolean => {
    return phoneNumber.startsWith("0");
  };

  /**
   * Formats a phone number with a country code
   */
  const formatWithCountryCode = (
    phoneNumber: string,
    countryCode: string
  ): string => {
    return `${countryCode}${removeLeadingZeros(phoneNumber)}`;
  };

  /**
   * Removes a given country code from a phone number
   */
  const removeCountryCode = (
    phoneNumber: string,
    countryCode: string
  ): string => {
    return phoneNumber.replace(new RegExp(`^${countryCode}`), "");
  };

  /**
   * Smartly split a phone number into country code and national number
   */
  const splitPhoneNumber = (
    phoneNumber?: string | null,
    defaultCountry: CountryCode = "US"
  ) => {
    if (!phoneNumber || typeof phoneNumber !== "string") {
      return { countryCode: null, nationalNumber: "", country: null };
    }

    try {
      const parsed = parsePhoneNumberFromString(phoneNumber, defaultCountry);

      if (!parsed) {
        return { countryCode: null, nationalNumber: phoneNumber, country: null };
      }

      return {
        countryCode: `+${parsed.countryCallingCode}`,
        nationalNumber: parsed.nationalNumber,
        country: parsed.country as CountryCode,
      };
    } catch {
      return { countryCode: null, nationalNumber: phoneNumber, country: null };
    }
  };

  /**
   * Validate if a phone number is correct for a given country
   */
  const validatePhoneNumber = (
    phoneNumber: string,
    country: CountryCode = "US"
  ): boolean => {
    try {
      return isValidPhoneNumber(phoneNumber, country);
    } catch {
      return false;
    }
  };

  /**
   * Format a phone number into international format (e.g. +233 24 356 7890)
   */
  const formatInternational = (
    phoneNumber: string,
    country: CountryCode = "US"
  ): string => {
    try {
      const parsed = parsePhoneNumberFromString(phoneNumber, country);
      return parsed ? parsed.formatInternational() : phoneNumber;
    } catch {
      return phoneNumber;
    }
  };

  /**
   * Format a phone number into national format (e.g. 024 356 7890)
   */
  const formatNational = (
    phoneNumber: string,
    country: CountryCode = "US"
  ): string => {
    try {
      const parsed = parsePhoneNumberFromString(phoneNumber, country);
      return parsed ? parsed.formatNational() : phoneNumber;
    } catch {
      return phoneNumber;
    }
  };

  /**
   * "As you type" formatting (great for inputs)
   */
  const formatAsYouType = (
    phoneNumber: string,
    country: CountryCode = "US"
  ) => {
    const formatter = new AsYouType(country);
    return formatter.input(phoneNumber);
  };

  /**
   * Normalize phone number into E.164 format (e.g. +233243567890)
   * Always safe for storage in DB
   */
  const normalizePhoneNumber = (
    phoneNumber: string,
    country?: CountryCode
  ): string | null => {
    try {
      const parsed = parsePhoneNumberFromString(phoneNumber, country);
      return parsed ? parsed.number : null; // returns E.164 format
    } catch {
      return null;
    }
  };

  /**
   * Get default country dynamically from browser locale
   */
  const getDefaultCountry = (): CountryCode => {
    try {
      const locale = navigator.language || "en-US"; // e.g. "en-GH"
      const country = locale.split("-")[1];
      return (country || "US") as CountryCode;
    } catch {
      return "US" as CountryCode;
    }
  };

  return {
    hasLeadingZeros,
    removeLeadingZeros,
    formatWithCountryCode,
    removeCountryCode,
    splitPhoneNumber,
    validatePhoneNumber,
    formatInternational,
    formatNational,
    formatAsYouType,
    normalizePhoneNumber,
    getDefaultCountry,
  };
}